import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLiveClass } from '../contexts/LiveClassContext';
import {
    ArrowLeft, CheckCircle, Lock, BookOpen, ChevronRight,
    Loader2, Trophy, AlertCircle, Play, ExternalLink, X, Menu, Printer, ShieldCheck, CreditCard,
    UploadCloud, FileText, MessageSquare, Video
} from 'lucide-react';

const API = process.env.REACT_APP_API_BASE_URL;

// ─── Markdown renderer (lightweight, no dependencies) ───────────────────────
const MarkdownRenderer = ({ content }) => {
    if (!content) return null;

    const renderLine = (line, i) => {
        // Headings
        if (line.startsWith('### ')) return <h3 key={i} className="text-base font-bold text-gray-800 mt-5 mb-1">{line.slice(4)}</h3>;
        if (line.startsWith('## ')) return <h2 key={i} className="text-lg font-bold text-indigo-900 mt-6 mb-2 border-b border-indigo-100 pb-1">{line.slice(3)}</h2>;
        if (line.startsWith('# ')) return <h1 key={i} className="text-xl font-bold text-indigo-900 mt-4 mb-3">{line.slice(2)}</h1>;
        // Bullet
        if (line.startsWith('- ') || line.startsWith('* ')) {
            const text = line.slice(2);
            return <li key={i} className="ml-4 text-sm text-gray-700 mb-1 list-disc">{renderInline(text)}</li>;
        }
        // Numbered
        if (/^\d+\. /.test(line)) {
            const text = line.replace(/^\d+\. /, '');
            return <li key={i} className="ml-4 text-sm text-gray-700 mb-1 list-decimal">{renderInline(text)}</li>;
        }
        // Horizontal rule
        if (line === '---') return <hr key={i} className="border-gray-200 my-4" />;
        // Empty line
        if (line.trim() === '') return <div key={i} className="h-2" />;
        // Regular paragraph
        return <p key={i} className="text-sm text-gray-700 mb-2 leading-relaxed">{renderInline(line)}</p>;
    };

    const renderInline = (text) => {
        const parts = [];
        let remaining = text;
        let key = 0;
        // Process bold, italic, links, inline code
        const pattern = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|\[(.+?)\]\((https?:\/\/[^\)]+)\))/g;
        let lastIndex = 0;
        let match;
        while ((match = pattern.exec(text)) !== null) {
            if (match.index > lastIndex) {
                parts.push(<span key={key++}>{text.slice(lastIndex, match.index)}</span>);
            }
            if (match[2]) parts.push(<strong key={key++} className="font-semibold text-gray-900">{match[2]}</strong>);
            else if (match[3]) parts.push(<em key={key++} className="italic">{match[3]}</em>);
            else if (match[4]) parts.push(<code key={key++} className="bg-gray-100 text-indigo-700 text-xs px-1 py-0.5 rounded font-mono">{match[4]}</code>);
            else if (match[5]) parts.push(
                <a key={key++} href={match[6]} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-indigo-600 hover:underline font-medium">
                    {match[5]} <ExternalLink className="w-3 h-3" />
                </a>
            );
            lastIndex = match.index + match[0].length;
        }
        if (lastIndex < text.length) parts.push(<span key={key++}>{text.slice(lastIndex)}</span>);
        return parts.length > 0 ? parts : text;
    };

    return (
        <div className="prose-sm max-w-none">
            {content.split('\n').map((line, i) => renderLine(line, i))}
        </div>
    );
};

// ─── Final Unit Quiz Component ───────────────────────────────────────────────
const FinalQuiz = ({ unitId, onPass }) => {
    const [quiz, setQuiz] = useState(null);
    const [loading, setLoading] = useState(true);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [showDetailedResults, setShowDetailedResults] = useState(false);

    useEffect(() => {
        const fetchQuiz = async () => {
            try {
                const res = await fetch(`${API}/self-paced/unit/${unitId}/final-quiz`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                if (res.ok) setQuiz(await res.json());
                else setQuiz(null);
            } catch { setQuiz(null); }
            finally { setLoading(false); }
        };
        fetchQuiz();
    }, [unitId]);

    const handleSubmit = async () => {
        const answersArr = Object.entries(answers).map(([questionId, answer]) => ({
            questionId: parseInt(questionId), answer
        }));
        if (answersArr.length < (quiz?.questions?.length || 0)) {
            alert('Please answer all questions before submitting.'); return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/self-paced/quiz/${quiz.id}/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify({ answers: answersArr })
            });
            const data = await res.json();
            setResult(data);
            if (data.passed) onPass(data);
        } catch (e) {
            alert('Submission failed. Please try again.');
        } finally { setSubmitting(false); }
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
            <p className="text-gray-600 font-medium">Loading your final assessment...</p>
        </div>
    );

    if (!quiz) return (
        <div className="text-center py-16">
            <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
            <p className="text-gray-600">The final quiz is being prepared. Please wait a moment and refresh.</p>
        </div>
    );

    if (result) {
        const correctCount = result.questionResults?.filter(r => r.correct).length || 0;
        const totalCount = result.questionResults?.length || 0;

        return (
            <div className="assessment-results-container">
                <style>{`
                    @media print {
                        .no-print, button, .roadmap-sidebar, .top-nav-bar, aside, nav { display: none !important; }
                        .assessment-results-container { margin: 0 !important; padding: 20px !important; width: 100% !important; }
                        body { background: white !important; }
                    }
                `}</style>

                {/* ── Summary card ── */}
                <div className={`rounded-2xl border-2 p-8 text-center mb-6 ${result.passed ? 'bg-gradient-to-b from-green-50 to-white border-green-200' : 'bg-gradient-to-b from-red-50 to-white border-red-200'}`}>
                    {result.passed ? (
                        <Trophy className="w-20 h-20 text-yellow-400 mx-auto mb-3 drop-shadow-md" />
                    ) : (
                        <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-3" />
                    )}
                    <h2 className={`text-3xl font-black mb-1 ${result.passed ? 'text-green-700' : 'text-red-700'}`}>
                        {result.passed ? '🎉 You Passed!' : 'Not Quite...'}
                    </h2>
                    {!result.passed && (
                        <p className="text-sm text-gray-500 mb-4">Score needed: <strong>{result.passingScore}%</strong> — Review your notes and try again.</p>
                    )}

                    {/* Score cards */}
                    <div className="grid grid-cols-3 gap-4 mt-6 max-w-lg mx-auto">
                        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                            <p className="text-3xl font-black text-indigo-600">{result.score}%</p>
                            <p className="text-xs text-gray-500 mt-1 font-medium uppercase tracking-wide">Score</p>
                        </div>
                        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                            <p className="text-3xl font-black text-gray-800">{result.earnedPoints}<span className="text-base font-semibold text-gray-400">/{result.totalPoints}</span></p>
                            <p className="text-xs text-gray-500 mt-1 font-medium uppercase tracking-wide">Points</p>
                        </div>
                        <div className={`rounded-xl border shadow-sm p-4 ${result.passed ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
                            <p className={`text-3xl font-black ${result.passed ? 'text-green-600' : 'text-red-500'}`}>{correctCount}<span className="text-base font-semibold text-gray-400">/{totalCount}</span></p>
                            <p className="text-xs text-gray-500 mt-1 font-medium uppercase tracking-wide">Correct</p>
                        </div>
                    </div>

                    {result.passed && (
                        <p className="mt-5 text-indigo-700 font-medium text-sm animate-pulse">
                            {result.nextUnit ? `🔓 Unlocking next unit: ${result.nextUnit.name}...` : '🏆 Course complete! Well done.'}
                        </p>
                    )}
                </div>

                {/* ── Action buttons ── */}
                <div className="flex flex-wrap gap-3 justify-center no-print mb-8">
                    {result.questionResults?.length > 0 && (
                        <button
                            onClick={() => setShowDetailedResults(prev => !prev)}
                            className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors flex items-center gap-2"
                        >
                            {showDetailedResults ? '▲ Hide Results' : '📋 View Results'}
                        </button>
                    )}
                    {!result.passed && (
                        <button onClick={() => setResult(null)} className="px-6 py-3 bg-white border-2 border-indigo-300 text-indigo-700 rounded-xl font-bold hover:bg-indigo-50 transition-colors">
                            🔄 Retry Quiz
                        </button>
                    )}
                    <button onClick={() => window.print()} className="px-6 py-3 bg-gray-800 text-white rounded-xl font-bold hover:bg-gray-900 transition-colors flex items-center gap-2">
                        <Printer className="w-4 h-4" /> Print
                    </button>
                </div>

                {/* ── Detailed results (expandable) ── */}
                {showDetailedResults && result.questionResults && (
                    <div className="space-y-4 text-left">
                        <h3 className="text-lg font-black text-gray-800 mb-3 flex items-center gap-2">
                            📝 Detailed Question Review
                            <span className="text-sm font-normal text-gray-400">({correctCount}/{totalCount} correct)</span>
                        </h3>
                        {result.questionResults.map((r, i) => (
                            <div key={i} className={`rounded-xl border p-5 ${r.correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                <div className="flex items-start gap-3">
                                    <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-black ${r.correct ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                                        {r.correct ? '✓' : '✗'}
                                    </span>
                                    <div className="flex-1">
                                        <p className={`text-sm font-bold mb-2 ${r.correct ? 'text-green-800' : 'text-red-800'}`}>
                                            Question {i + 1}: {r.correct ? 'Correct' : 'Incorrect'}
                                        </p>
                                        {!r.correct && (
                                            <div className="bg-white rounded-lg px-4 py-2 text-sm mb-2 border border-gray-100">
                                                <span className="text-gray-500">Correct answer: </span>
                                                <strong className="text-gray-800">{r.correctAnswer}</strong>
                                            </div>
                                        )}
                                        {r.explanation && (
                                            <p className="text-xs text-gray-500 italic mt-1 leading-relaxed">
                                                💡 {r.explanation}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div>
            <div className="mb-6">
                <h2 className="text-xl font-black text-gray-900">{quiz.title}</h2>
                <p className="text-sm text-gray-500 mt-1">{quiz.description} — Pass mark: {quiz.passingScore}%</p>
            </div>
            <div className="space-y-6">
                {quiz.questions?.sort((a, b) => a.order - b.order).map((q) => (
                    <div key={q.id} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                        <p className="font-semibold text-gray-900 mb-3">{q.order + 1}. {q.text}</p>
                        {q.type === 'multiple_choice' && q.options && (
                            <div className="space-y-2">
                                {q.options.map((opt, i) => (
                                    <label key={i} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${answers[q.id] === opt ? 'bg-indigo-50 border-indigo-400' : 'border-gray-100 hover:border-gray-300'}`}>
                                        <input type="radio" name={`q-${q.id}`} value={opt} checked={answers[q.id] === opt}
                                            onChange={() => setAnswers(prev => ({ ...prev, [q.id]: opt }))}
                                            className="text-indigo-600" />
                                        <span className="text-sm text-gray-800">{opt}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                        {q.type === 'true_false' && (
                            <div className="flex gap-3">
                                {['True', 'False'].map(opt => (
                                    <label key={opt} className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg cursor-pointer border transition-colors ${answers[q.id] === opt ? 'bg-indigo-50 border-indigo-400' : 'border-gray-100 hover:border-gray-300'}`}>
                                        <input type="radio" name={`q-${q.id}`} value={opt} checked={answers[q.id] === opt}
                                            onChange={() => setAnswers(prev => ({ ...prev, [q.id]: opt }))} />
                                        <span className="text-sm font-medium">{opt}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                        {q.type === 'short_answer' && (
                            <textarea rows={2} placeholder="Type your answer..."
                                className="w-full mt-1 p-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none resize-none"
                                value={answers[q.id] || ''}
                                onChange={e => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))} />
                        )}
                    </div>
                ))}
            </div>
            <button onClick={handleSubmit} disabled={submitting}
                className="mt-8 w-full py-4 bg-indigo-600 text-white rounded-xl font-black text-base hover:bg-indigo-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Grading...</> : '🎯 Submit Final Assessment'}
            </button>
        </div>
    );
};

// ─── Main Self-Paced View ────────────────────────────────────────────────────
const SelfPacedUnitView = () => {
    const { unitId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { handleJoinSession } = useLiveClass();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeLesson, setActiveLesson] = useState(null);
    const [completing, setCompleting] = useState(false);
    const [showQuiz, setShowQuiz] = useState(false);
    const [finalQuizData, setFinalQuizData] = useState(null); // { quizId, title }
    const [unitJustCompleted, setUnitJustCompleted] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [enrolling, setEnrolling] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    const handleEnroll = async () => {
        setEnrolling(true);
        try {
            const res = await fetch(`${API}/enrollments/enroll`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}` 
                },
                body: JSON.stringify({ unitId })
            });
            const result = await res.json();
            if (result.requiresPayment) {
                navigate(`/payment/${unitId}`);
            } else {
                await fetchLessons();
            }
        } catch (e) {
            alert('Enrollment failed. Please try again.');
        } finally {
            setEnrolling(false);
        }
    };

    const fetchLessons = useCallback(async () => {
        try {
            const res = await fetch(`${API}/self-paced/unit/${unitId}/lessons`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            if (!res.ok) throw new Error('Failed to load lessons');
            const d = await res.json();
            setData(d);
            // Default to first unlocked incomplete lesson
            const active = d.lessons.find(l => l.isUnlocked && !l.isCompleted) || d.lessons[d.lessons.length - 1];
            setActiveLesson(active);
        } catch (e) {
            console.error(e);
        } finally { setLoading(false); }
    }, [unitId]);

    useEffect(() => { fetchLessons(); }, [fetchLessons]);

    // Polling for live class status
    useEffect(() => {
        if (!unitId || !data) return;

        const checkLiveStatus = async () => {
            try {
                const res = await fetch(`${API}/livekit/unit/${unitId}/status`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                if (res.ok) {
                    const status = await res.json();
                    setData(prev => {
                        if (!prev) return prev;
                        if (prev.unit.isLive === status.isLive) return prev;
                        return {
                            ...prev,
                            unit: { ...prev.unit, isLive: status.isLive }
                        };
                    });
                }
            } catch (err) {
                console.error('Error polling live status:', err);
            }
        };

        const interval = setInterval(checkLiveStatus, 10000); // Check every 10 seconds
        checkLiveStatus(); // Check immediately on mount/data load

        return () => clearInterval(interval);
    }, [unitId, data === null]);

    const handleCompleteLesson = async () => {
        if (!activeLesson || completing) return;
        setCompleting(true);
        try {
            const res = await fetch(`${API}/self-paced/lesson/${activeLesson.id}/complete`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            const result = await res.json();
            if (!res.ok) { alert(result.message || 'Error completing lesson'); return; }

            if (result.isLastLesson) {
                // Final quiz was auto-generated — show it
                setFinalQuizData(result.finalQuiz);
                setShowQuiz(true);
            } else {
                // Move to next lesson
                await fetchLessons();
            }
        } catch (e) {
            alert('Network error. Please try again.');
        } finally { setCompleting(false); }
    };

    const handleQuizPass = (result) => {
        setUnitJustCompleted(true);
        // We no longer auto-navigate here. 
        // This allows the student to stay and review their answers inline using the new results UI.
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
                <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-500">Loading your lesson...</p>
            </div>
        </div>
    );

    if (!data) return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
                <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Could not load lessons for this unit.</p>
                <button onClick={() => navigate('/units')} className="mt-4 px-5 py-2 bg-indigo-600 text-white rounded-lg">
                    Back to Units
                </button>
            </div>
        </div>
    );

    const { unit, lessons, unitStatus, unitProgress, paymentStatus } = data;
    const isAccessible = unit.isFree || (unitStatus !== 'not_enrolled' && paymentStatus === 'confirmed');
    const allDone = lessons.every(l => l.isCompleted);
    const unitCompleted = unitStatus === 'completed' || unitStatus === 'passed';
    const isLastLesson = activeLesson && lessons[lessons.length - 1]?.id === activeLesson.id;

    if (!isAccessible) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-40 shadow-sm">
                    <button 
                      onClick={() => navigate('/units', { state: location.state })} 
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <h1 className="font-bold text-gray-900 truncate text-sm sm:text-base">Enrollment Required</h1>
                </div>

                <div className="flex-1 flex items-center justify-center p-6">
                    <div className="max-w-md w-full bg-white rounded-3xl border border-gray-200 shadow-xl overflow-hidden">
                        <div className="bg-indigo-600 p-8 text-center">
                            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                                {unitStatus === 'not_enrolled' ? (
                                    <ShieldCheck className="w-10 h-10 text-white" />
                                ) : (
                                    <CreditCard className="w-10 h-10 text-white" />
                                )}
                            </div>
                            <h2 className="text-2xl font-black text-white mb-2">{unit.name}</h2>
                            <p className="text-indigo-100 text-sm">
                                {unitStatus === 'not_enrolled' 
                                    ? "Enroll today to unlock the full curriculum and start your learning journey." 
                                    : "Completion of payment is required to access these lessons."}
                            </p>
                        </div>
                        
                        <div className="p-8">
                            <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-100">
                                <span className="text-gray-500 font-medium">Registration Fee</span>
                                <span className="text-2xl font-black text-gray-900">
                                    {unit.cost ? `${unit.currency} ${unit.cost.toLocaleString()}` : "TBD"}
                                </span>
                            </div>

                            <button
                                onClick={unitStatus === 'not_enrolled' ? handleEnroll : () => navigate(`/payment/${unitId}`)}
                                disabled={enrolling}
                                className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black text-lg hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 flex items-center justify-center gap-3 disabled:opacity-70"
                            >
                                {enrolling ? <Loader2 className="w-6 h-6 animate-spin" /> : (
                                    unitStatus === 'not_enrolled' ? "Enroll Now" : "Pay Now"
                                )}
                                <ChevronRight className="w-6 h-6" />
                            </button>
                            
                            <button 
                                onClick={() => navigate('/units', { state: location.state })}
                                className="w-full mt-4 py-2 text-gray-400 font-bold hover:text-gray-600 transition-colors text-sm"
                            >
                                Back to Course Catalog
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            {/* Top nav */}
            <div className={`bg-white border-b border-gray-200 px-4 transition-all duration-300 flex items-center gap-3 sticky top-0 z-40 shadow-sm ${isScrolled ? 'py-1 sm:py-2' : 'py-3'}`}>
                <button 
                  onClick={() => navigate('/units', { state: location.state })} 
                  className={`rounded-lg hover:bg-gray-100 transition-colors ${isScrolled ? 'p-1 sm:p-2' : 'p-2'}`}
                >
                    <ArrowLeft className={`text-gray-600 transition-all duration-300 ${isScrolled ? 'w-4 h-4 sm:w-5 sm:h-5' : 'w-5 h-5'}`} />
                </button>
                <div className="flex-1 min-w-0 transition-all duration-300">
                    <h1 className={`font-bold text-gray-900 truncate transition-all duration-300 ${isScrolled ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'}`}>{unit.name}</h1>
                    <div className={`flex items-center gap-2 mt-0.5 transition-all duration-300 overflow-hidden ${isScrolled ? 'max-h-0 opacity-0' : 'max-h-8 opacity-100'}`}>
                        <div className="flex-1 bg-gray-200 rounded-full h-1.5 max-w-xs">
                            <div className="bg-indigo-600 h-1.5 rounded-full transition-all duration-700"
                                style={{ width: `${unitProgress || 0}%` }} />
                        </div>
                        <span className="text-xs text-gray-500 font-medium">{unitProgress || 0}%</span>
                    </div>
                </div>
                
                {/* Live session top header indicator (optimized for mobile scroll) */}
                {unit?.isLive && isScrolled && (
                    <button onClick={() => handleJoinSession(unitId)} className="sm:hidden flex items-center justify-center p-1.5 bg-red-50 text-red-600 rounded-lg animate-pulse" title="Live Session Active">
                        <Video className="w-4 h-4" />
                    </button>
                )}

                <button 
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className={`sm:hidden rounded-lg hover:bg-gray-100 text-indigo-600 flex items-center transition-all ${isScrolled ? 'p-1.5 gap-1' : 'p-2 gap-2'}`}
                >
                  <Menu className={`transition-all duration-300 ${isScrolled ? 'w-4 h-4' : 'w-5 h-5'}`} />
                  <span className={`text-[10px] sm:text-xs font-bold uppercase tracking-wider transition-all overflow-hidden ${isScrolled ? 'max-w-0 opacity-0 hidden' : 'max-w-[100px] opacity-100'}`}>Lessons</span>
                </button>
                <BookOpen className={`text-indigo-400 hidden sm:block transition-all ${isScrolled ? 'w-4 h-4' : 'w-5 h-5'}`} />

            </div>

            <div className="flex flex-1 overflow-hidden relative">
                {/* ── MOBILE OVERLAY ── */}
                {sidebarOpen && (
                    <div 
                        className="fixed inset-0 bg-black/30 z-40 sm:hidden transition-opacity"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* ── LEFT SIDEBAR: Lesson Roadmap ── */}
                <div className={`
                    absolute sm:relative z-50 sm:z-auto
                    w-72 sm:w-64 bg-white border-r border-gray-200 flex-shrink-0 h-full flex flex-col
                    transform transition-transform duration-300 ease-in-out
                    ${sidebarOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'}
                `}>
                    <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/80">
                        <h2 className="text-xs font-black uppercase tracking-widest text-gray-500">Lesson Roadmap</h2>
                        <button onClick={() => setSidebarOpen(false)} className="sm:hidden p-1 text-gray-400 hover:text-gray-900 rounded-full hover:bg-gray-200">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="flex-1 p-3 space-y-1">
                        {lessons.map((lesson, index) => {
                            const isActive = activeLesson?.id === lesson.id && !showQuiz;
                            return (
                                <button
                                    key={lesson.id}
                                    onClick={() => {
                                        if (lesson.isUnlocked) { 
                                            setActiveLesson(lesson); 
                                            setShowQuiz(false); 
                                            setSidebarOpen(false); // Auto-close on mobile
                                        }
                                    }}
                                    disabled={!lesson.isUnlocked}
                                    className={`w-full text-left p-3 rounded-xl transition-all flex items-start gap-3 group ${isActive
                                        ? 'bg-indigo-600 text-white shadow-md'
                                        : lesson.isCompleted
                                            ? 'bg-green-50 text-green-800 hover:bg-green-100'
                                            : lesson.isUnlocked
                                                ? 'bg-white hover:bg-indigo-50 text-gray-700 border border-gray-100'
                                                : 'opacity-40 cursor-not-allowed bg-gray-50 text-gray-400'
                                        }`}
                                >
                                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-black mt-0.5 ${isActive ? 'bg-white text-indigo-600'
                                        : lesson.isCompleted ? 'bg-green-600 text-white'
                                            : lesson.isUnlocked ? 'bg-indigo-100 text-indigo-600'
                                                : 'bg-gray-200 text-gray-400'
                                        }`}>
                                        {lesson.isCompleted ? <CheckCircle className="w-4 h-4" /> : !lesson.isUnlocked ? <Lock className="w-3 h-3" /> : index + 1}
                                    </div>
                                    <span className={`text-xs font-semibold leading-snug ${isActive ? 'text-white' : ''}`}>{lesson.title}</span>
                                </button>
                            );
                        })}

                        {/* Final quiz step */}
                        {allDone && (
                            <button
                                onClick={() => { setShowQuiz(true); setSidebarOpen(false); }}
                                className={`w-full text-left p-3 rounded-xl transition-all flex items-start gap-3 ${
                                    showQuiz ? 'bg-yellow-500 text-white shadow-md' : 
                                    unitCompleted ? 'bg-green-50 text-green-800 border border-green-200' :
                                    'bg-yellow-50 text-yellow-800 hover:bg-yellow-100 border border-yellow-200'
                                }`}
                            >
                                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                                    unitCompleted ? 'bg-green-500 text-white' : 'bg-yellow-400 text-white'
                                }`}>
                                    {unitCompleted ? <CheckCircle className="w-3.5 h-3.5" /> : <Trophy className="w-3.5 h-3.5" />}
                                </div>
                                <span className="text-xs font-semibold">
                                    {unitCompleted ? 'Assessment Completed' : 'Final Assessment'}
                                </span>
                            </button>
                        )}
                    </div>
                </div>

                {/* ── MAIN CONTENT ── */}
                <div 
                    className="flex-1 overflow-y-auto"
                    onScroll={(e) => setIsScrolled(e.target.scrollTop > 30)}
                >
                    {showQuiz ? (
                        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
                            <FinalQuiz unitId={unitId} onPass={handleQuizPass} />
                        </div>
                    ) : activeLesson ? (
                        <>
                            {/* Academic Resources (Sticky) */}
                            <div className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur-sm border-b border-gray-200 shadow-sm w-full">
                                <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-2 pb-3">
                                    <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2.5 ml-1">Academic Resources</h3>
                                    <div className="flex overflow-x-auto pb-1 gap-3 snap-x hide-scrollbar">
                                        <button 
                                            onClick={() => unit?.isLive ? handleJoinSession(unitId) : null}
                                            disabled={!unit?.isLive}
                                            className={`snap-start flex-shrink-0 w-64 p-3 bg-white border border-gray-100 rounded-xl transition-all flex items-start gap-3 group text-left relative overflow-hidden ${unit?.isLive ? 'hover:shadow-md hover:border-red-200 cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
                                        >
                                            <div className="p-2 bg-red-50 rounded-lg text-red-600 group-hover:scale-110 transition-transform relative z-10">
                                                <Video className="w-5 h-5" />
                                            </div>
                                            <div className="relative z-10">
                                                <div className="font-bold text-gray-900 text-sm">Virtual Class</div>
                                                <div className="text-[10px] text-gray-500 mt-0.5">{unit?.isLive ? 'Session is live' : 'No active session'}</div>
                                            </div>
                                            {unit?.isLive && (
                                                <>
                                                    <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-red-500 animate-pulse z-10" />
                                                    <div className="absolute inset-0 bg-red-50/20 z-0" />
                                                </>
                                            )}
                                        </button>

                                        <button className="snap-start flex-shrink-0 w-64 p-3 bg-gradient-to-br from-indigo-50/50 to-blue-50/50 border border-blue-100 rounded-xl hover:shadow-md hover:border-blue-200 transition-all flex items-start gap-3 group text-left">
                                            <div className="p-2 bg-white rounded-lg shadow-sm text-blue-600 group-hover:scale-110 transition-transform">
                                                <UploadCloud className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <div className="font-bold text-gray-900 text-sm">Unit Materials</div>
                                                <div className="text-[10px] text-gray-500 mt-0.5">Share notes & resources</div>
                                            </div>
                                        </button>
                                        
                                        <button className="snap-start flex-shrink-0 w-64 p-3 bg-white border border-gray-100 rounded-xl hover:shadow-md hover:border-green-200 transition-all flex items-start gap-3 group text-left">
                                            <div className="p-2 bg-green-50 rounded-lg text-green-600 group-hover:scale-110 transition-transform">
                                                <FileText className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <div className="font-bold text-gray-900 text-sm">Assignments</div>
                                                <div className="text-[10px] text-gray-500 mt-0.5">Create tasks & quizzes</div>
                                            </div>
                                        </button>

                                        <button className="snap-start flex-shrink-0 w-64 p-3 bg-white border border-gray-100 rounded-xl hover:shadow-md hover:border-purple-200 transition-all flex items-start gap-3 group text-left">
                                            <div className="p-2 bg-purple-50 rounded-lg text-purple-600 group-hover:scale-110 transition-transform">
                                                <MessageSquare className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <div className="font-bold text-gray-900 text-sm">Discussion</div>
                                                <div className="text-[10px] text-gray-500 mt-0.5">Student interactions</div>
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-6 pb-12">
                                {/* Lesson Header */}
                                <div className="mb-6">
                                    <div className="flex items-center gap-2 text-xs text-indigo-500 font-bold uppercase tracking-widest mb-2">
                                        <Play className="w-3 h-3" />
                                        Lesson {activeLesson.order}
                                    </div>
                                    <h2 className="text-2xl font-black text-gray-900">{activeLesson.title}</h2>
                                </div>

                                {/* Notes Content */}
                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8 mb-8">
                                    {activeLesson.isCompleted && (
                                        <div className="mb-4 flex items-center gap-2 text-green-600 text-sm font-semibold bg-green-50 rounded-lg px-3 py-2">
                                            <CheckCircle className="w-4 h-4" /> Lesson completed
                                        </div>
                                    )}
                                    <MarkdownRenderer content={activeLesson.description} />
                                </div>

                                {/* Action Footer */}
                                {!activeLesson.isCompleted ? (
                                    <button
                                        onClick={handleCompleteLesson}
                                        disabled={completing}
                                        className="w-full py-4 bg-indigo-600 text-white rounded-xl font-black text-base hover:bg-indigo-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg shadow-indigo-200"
                                    >
                                        {completing ? (
                                            <><Loader2 className="w-5 h-5 animate-spin" /> {isLastLesson ? 'Saving progress, fetching your quiz...' : 'Saving progress...'}</>
                                        ) : allDone ? (
                                            <><Trophy className="w-5 h-5" /> Take Final Assessment</>
                                        ) : (
                                            <>Mark Complete & Continue <ChevronRight className="w-5 h-5" /></>
                                        )}
                                    </button>
                                ) : (
                                    <div className="text-center">
                                        {unitCompleted ? (
                                            <button
                                                onClick={() => {
                                                    // Navigate to the assignments page deep-linked to the QUIZZES tab for this specific unit
                                                    // Use the 'tab' and 'unitId' query params supported by Assignments.js
                                                    navigate(`/assignments?tab=quizzes&unitId=${unitId}`);
                                                }}
                                                className="px-8 py-4 bg-green-600 text-white rounded-xl font-black text-base hover:bg-green-700 transition-colors shadow-lg shadow-green-200 flex items-center gap-2 mx-auto"
                                            >
                                                <CheckCircle className="w-5 h-5" /> Assessment Passed
                                            </button>
                                        ) : allDone ? (
                                            <button
                                                onClick={() => setShowQuiz(true)}
                                                className="px-8 py-4 bg-yellow-500 text-white rounded-xl font-black text-base hover:bg-yellow-600 transition-colors shadow-lg shadow-yellow-200 flex items-center gap-2 mx-auto"
                                            >
                                                <Trophy className="w-5 h-5" /> Take Final Assessment
                                            </button>
                                        ) : (
                                            <p className="text-gray-400 text-sm">Select the next lesson from the sidebar.</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                        ) : (
                        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 w-full">
                            <div className="text-center py-20">
                                <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500">No lessons available for this unit yet.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SelfPacedUnitView;
